/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import { Control } from 'react-hook-form';
import { WorkOrderYesNoType } from '../../../../API';
import { DayOfWeek, PROCESS_EVENTS } from '../../../../Shared/Constants/App';
import { FiltersState } from '../../../../Shared/Store/features/filtersSlice';
import { Dispatch, SetStateAction } from 'react';
import { IProcessEvents } from '../../../../routes/config';

export interface ISupervisorAssociate {
  woScheduleMasterId: string;
  name: string;
  roleName: string;
  email: string;
}

export enum DropdownOptions {
  RescheduleShift = 'RescheduleShift',
  CancelShift = 'CancelShift',
  ModifySchedule = 'ModifySchedule',
  ModifyVendor = 'ModifyVendor',
  RemoveVendor = 'RemoveVendor',
  ReassignVendor = 'ReassignVendor',
  ModifyGeoFence = 'Modify GeoFence',
  ManageAssociatesAndSupervisors = 'ManageAssociatesAndSupervisors',
  RemoveSupervisors = 'RemoveSupervisors',
  RemoveAssociates = 'RemoveAssociates',
  AssignSupervisors = 'AssignSupervisors',
  AssignAssociates = 'AssignAssociates',
  AdjustProof = 'AdjustProof',
  CreateJobSchedule = 'CreateJobSchedule',
  CreateAdhocShifts = 'CreateAdhocShifts',
  CreatePeriodicJob = 'CreatePeriodicJob',
  ModifyServices = 'ModifyServices',
  CreateRetrospectiveShift = 'CreateRetrospectiveShift',
}

export const DROPDOWN_EVENT_MAP: Partial<Record<DropdownOptions, string>> = {
  [DropdownOptions.RescheduleShift]: PROCESS_EVENTS.RescheduleShift,
  [DropdownOptions.CancelShift]: PROCESS_EVENTS.CancelShift,
  [DropdownOptions.ModifySchedule]: PROCESS_EVENTS.ModifySchedule,
  [DropdownOptions.ManageAssociatesAndSupervisors]: PROCESS_EVENTS.ManageAssociatesAndSupervisors,
  [DropdownOptions.AdjustProof]: PROCESS_EVENTS.AdjustProof,
  [DropdownOptions.CreateAdhocShifts]: PROCESS_EVENTS.CreateAdhocShifts,
  [DropdownOptions.RemoveVendor]: PROCESS_EVENTS.RemoveVendor,
  [DropdownOptions.ReassignVendor]: PROCESS_EVENTS.ReassignVendor,
  [DropdownOptions.ModifyGeoFence]: PROCESS_EVENTS.ModifyGeoFence,
  [DropdownOptions.CreateJobSchedule]: PROCESS_EVENTS.CreateJobSchedule,
  [DropdownOptions.CreatePeriodicJob]: PROCESS_EVENTS.CreatePeriodicJobs,
  [DropdownOptions.ModifyServices]: PROCESS_EVENTS.ModifyServices,
  [DropdownOptions.CreateRetrospectiveShift]: PROCESS_EVENTS.CreateRetrospectiveShift,
};

export enum CancellationOptions {
  PermanentClosure = 'permanent',
  TemporaryClosure = 'temporary',
  ShiftCancellation = 'shiftCancellation',
}
export interface IModifySchedulePopupProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: {
    woScheduleMasterId: string;
    scheduleDays: any;
    startTime: string;
    endTime: string;
    shiftType: string;
    scheduleName: string;
    endDateOffset: number;
    totalFTE: number;
    scheduleStartDate: Date | null;
    scheduleEndDate: Date | null;
  }) => void;
  selectedJobData: any;
  setOpenPopup: React.Dispatch<React.SetStateAction<boolean>>;
  selectedRows: any;
  isDeletePopupVisible: boolean;
  setIsDeletePopupVisible: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface IDropdownWithButtonProps {
  selectedRows?: any[];
  type?: string;
  setSelectedDropDownValue?: (DropdownOptions: any) => void;
  selectedDropDownValue?: DropdownOptions | '';
  filters?: FiltersState;
  processEvents?: IProcessEvents[];
}

export interface IUserActionDropdownProps {
  type?: string | null;
}

export interface IUser {
  userId: string;
  username: string;
  roleId: string;
  roleName: string;
  organizationId: string;
  teamId: string;
  intId: string;
  email?: string;
}

export enum CreateUserOptions {
  Generate = 'generate',
  SetPassword = 'setPassword',
  Invite = 'invite',
}

export interface IAdjustProofModalProps {
  open: boolean;
  onClose: () => void;
  selectedJobs: any[];
  setRefreshKey?: Dispatch<SetStateAction<number>>;
}

export interface ISelectedJob {
  workOrderId: string;
  woScheduleMasterId: string;
  scheduleName: string;
  jobName: string;
}

export interface IServiceTask {
  mdTaskId: string;
  mdTaskName: string;
  woScheduleServiceId: string;
  isProofOfCompletionRequired: string;
  checked?: boolean;
}

export interface IServiceData {
  jobName: string;
  scheduleName: string;
  mdServiceId: string;
  mdServiceName: string;
  tasks: IServiceTask[];
}

export interface IRadioOption {
  value: WorkOrderYesNoType;
  label: string;
}

export interface IAdjustProofRequirementsPopupProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  selectedOption: WorkOrderYesNoType | null;
  onOptionChange: (value: WorkOrderYesNoType) => void;
  isUpdating: boolean;
  title: string;
  options: IRadioOption[];
  cancelButtonText: string;
  confirmButtonText: string;
}

export const PatternTypes = {
  Monthly: 'Monthly',
  Weekly: 'Weekly',
  SpecificDay: 'Specific-day',
} as const;

export const MonthlyPatternTypes = {
  Weekly: 'Weekly',
  SpecificDates: 'Specific dates',
} as const;

export type PatternType = (typeof PatternTypes)[keyof typeof PatternTypes];

export type MonthlyPatternType = (typeof MonthlyPatternTypes)[keyof typeof MonthlyPatternTypes];

interface QuarterConfig {
  month: string;
  week: string;
  day: string;
}

export interface IFormValues {
  startTime: string;
  endTime: string;
  woScheduleMasterId: string;
  scheduleDays: string | string[];
  shiftType: string;
  scheduleName: string;
  endDateOffset: number;
  selectedQuarter: string;
  selectedMonth: string;
  selectedWeek: string;
  selectedDay: string;
  quarters: {
    Q1: QuarterConfig;
    Q2: QuarterConfig;
    Q3: QuarterConfig;
    Q4: QuarterConfig;
  };
  patternType: PatternType;
  monthlyPatternType: MonthlyPatternType;
  shiftStartTime?: string;
  shiftEndTime?: string;
  AnnualDay?: string;
  scheduleStartDate?: Date | null;
  scheduleEndDate?: Date | null;
  totalFTE?: number | null;
}

export interface ICreateScheduleValues {
  startTime: string;
  endTime: string;
  woScheduleMasterId: string;
  scheduleDays: string | string[];
  shiftType: string;
  scheduleName: string;
  endDateOffset: number;
  selectedQuarter: string;
  selectedMonth: string;
  selectedWeek: string;
  selectedDay: string;
  annualDate: string;
  quarters: {
    Q1: QuarterConfig;
    Q2: QuarterConfig;
    Q3: QuarterConfig;
    Q4: QuarterConfig;
  };
  patternType: PatternType;
  monthlyPatternType: MonthlyPatternType;
  shiftStartTime?: string;
  shiftEndTime?: string;
}

export const WEEK_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const monthOptions = ['M1', 'M2', 'M3'];

export const monthlyOptions = {
  'Quarter 1': ['Jan', 'Feb', 'Mar'],
  'Quarter 2': ['Apr', 'May', 'Jun'],
  'Quarter 3': ['Jul', 'Aug', 'Sep'],
  'Quarter 4': ['Oct', 'Nov', 'Dec'],
  'All Quarters': ['Jan - Apr - Jul - Oct', 'Feb - May - Aug - Nov', 'Mar - Jun - Sep - Dec'],
};

export const quarterOptions = ['Q1', 'Q2', 'Q3', 'Q4'];

export const quarterlyOptions = ['Quarter 1', 'Quarter 2', 'Quarter 3', 'Quarter 4'];

export const weekNumbers = ['0', '1', '2', '3', '4', '5', '6'];

export const numericToDayMap = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export const allMonthsOptions = [
  { value: 1, label: 'Jan' },
  { value: 2, label: 'Feb' },
  { value: 3, label: 'Mar' },
  { value: 4, label: 'Apr' },
  { value: 5, label: 'May' },
  { value: 6, label: 'Jun' },
  { value: 7, label: 'Jul' },
  { value: 8, label: 'Aug' },
  { value: 9, label: 'Sep' },
  { value: 10, label: 'Oct' },
  { value: 11, label: 'Nov' },
  { value: 12, label: 'Dec' },
];

export interface IFieldProps {
  control: Control<any>;
  quarterName?: string;
  onFieldChange?: (fieldType: string, value: string) => void;
  selectProps?: any;
  patternType?: any;
  createSchedule?: boolean;
}

export interface IPatternFieldsProps extends IFieldProps {
  patternType: PatternType;
  createSchedule?: boolean;
}

export const GenerateWeekOptions = (patternType?: PatternType) => {
  const weekCount = patternType === PatternTypes.Weekly ? 15 : 5;
  return Array.from({ length: weekCount }, (_, i) => ({
    label: `Week ${i + 1}`,
    value: `W${i + 1}`,
  }));
};

export const getDayName = (dayIndex: number): string => {
  return DayOfWeek[dayIndex] ?? '-';
};

export const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const ordinalSuffix = (num: number): string => {
  if (num > 3 && num < 21) return `${num}th`;
  switch (num % 10) {
    case 1:
      return `${num}st`;
    case 2:
      return `${num}nd`;
    case 3:
      return `${num}rd`;
    default:
      return `${num}th`;
  }
};

export const getWeekName = (week: string): string => {
  const weekNum = Number(week);
  switch (weekNum) {
    case 1:
      return 'First week';
    case 2:
      return 'Second week';
    case 3:
      return 'Third week';
    case 4:
      return 'Fourth week';
    case 5:
      return 'Fifth week';
    default:
      return `Week ${week}`;
  }
};

export interface IOpenPosition {
  isPositionOpen: boolean;
  totalPositionsOpen: number;
}

export enum Source {
  OnTheJob = 'O',
  Peazy = 'P',
}

export type UpdateScheduleMasterInput = {
  woScheduleMasterId?: string | null;
  scheduleName?: string | null;
  scheduleStartDate?: Date | null;
  scheduleEndDate?: Date | null;
  scheduleDays?: string | null;
  scheduleStartTime?: string | null;
  scheduleEndTime?: string | null;
  endDateOffset?: number | null;
  punchInGraceMinutes?: number | null;
  punchOutGraceMinutes?: number | null;
  mdShiftType?: string | null;
  totalFTE?: number | null;
};

export interface ScheduleData {
  jobName: string;
  scheduleName: string;
  woScheduleMasterId: string;
  jobNumber: string;
}
